import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Group,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Text,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { AppModal } from '../../shared/components/AppModal';
import { ApiError } from '../../shared/api/problem';
import { listItems } from '../items/api';
import { saveSalesOrderLine, type SalesOrderLineDto } from './api';

interface Props {
  opened: boolean;
  onClose: () => void;
  salesOrderId: string;
  /** Null to add. */
  line: SalesOrderLineDto | null;
  onSaved: () => void;
}

/**
 * One order line.
 *
 * The price is prefilled from the item but left editable, because until M5.13–M5.15 land there is
 * no pricing engine and a list price is the only price there is. Leaving it read-only would mean a
 * salesperson could not honour a quote; hiding it would mean they could not see what the customer
 * is being charged.
 */
export function SalesOrderLineModal({ opened, onClose, salesOrderId, line, onSaved }: Props) {
  const { t } = useTranslation();
  const isEdit = line !== null;

  const [itemId, setItemId] = useState<string | null>(null);
  const [quantity, setQuantity] = useState<number | string>(1);
  const [unitPrice, setUnitPrice] = useState<number | string>('');
  const [error, setError] = useState<string | null>(null);
  const [itemError, setItemError] = useState<string | null>(null);

  const items = useQuery({
    queryKey: ['items', 'for-order'] as const,
    queryFn: ({ signal }) => listItems(false, signal),
    enabled: opened,
  });

  useEffect(() => {
    if (!opened) return;
    setError(null);
    setItemError(null);
    setItemId(line?.itemId ?? null);
    setQuantity(line?.quantityOrdered ?? 1);
    setUnitPrice(line?.unitPrice ?? '');
  }, [opened, line]);

  // Adding a line: choosing an item fills in its list price. Editing one leaves the price alone,
  // because whatever is on the line was agreed with the customer and re-deriving it would quietly
  // undo a discount somebody typed.
  useEffect(() => {
    if (!opened || isEdit || !itemId || !items.data) return;
    const item = items.data.find((i) => i.itemId === itemId);
    setUnitPrice(item?.unitPrice ?? 0);
  }, [opened, isEdit, itemId, items.data]);

  const itemOptions = useMemo(
    () =>
      (items.data ?? [])
        .filter((i) => i.isActive || i.itemId === line?.itemId)
        .map((i) => ({ value: i.itemId, label: `${i.itemNumber} — ${i.description}` })),
    [items.data, line?.itemId],
  );

  const save = useMutation({
    mutationFn: () =>
      saveSalesOrderLine(salesOrderId, {
        ...(isEdit ? { salesOrderLineId: line!.salesOrderLineId } : {}),
        itemId: itemId!,
        quantityOrdered: Number(quantity),
        unitPrice: unitPrice === '' ? null : Number(unitPrice),
      }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: (err) => setError(describe(err)),
  });

  function describe(err: unknown): string {
    if (err instanceof ApiError) {
      if (err.problem.type?.includes('below_shipped')) {
        return err.problem.detail ?? t('salesOrders.errors.belowShipped');
      }
      if (err.problem.type?.includes('order_closed')) {
        return t('salesOrders.errors.orderClosed');
      }
      return err.problem.detail ?? err.problem.title ?? t('salesOrders.errors.lineSaveFailed');
    }
    return t('salesOrders.errors.lineSaveFailed');
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!itemId) {
      setItemError(t('salesOrders.errors.itemRequired'));
      return;
    }
    setItemError(null);

    if (Number(quantity) <= 0) {
      setError(t('salesOrders.errors.quantityRequired'));
      return;
    }

    save.mutate();
  }

  const lineTotal = Number(quantity || 0) * Number(unitPrice || 0);

  return (
    <AppModal
      opened={opened}
      onClose={onClose}
      title={isEdit ? t('salesOrders.editLineTitle', { number: line!.lineNumber }) : t('salesOrders.addLineTitle')}
      width="md"
    >
      <form onSubmit={onSubmit} noValidate>
        <Stack gap="md">
          <Select
            label={t('salesOrders.item')}
            placeholder={t('salesOrders.itemPlaceholder')}
            data={itemOptions}
            value={itemId}
            onChange={setItemId}
            error={itemError}
            disabled={items.isLoading}
            searchable
            required
            autoFocus
          />

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <NumberInput
              label={t('salesOrders.quantity')}
              value={quantity}
              onChange={setQuantity}
              min={0}
              decimalScale={4}
              required
            />
            <NumberInput
              label={t('salesOrders.unitPrice')}
              description={t('salesOrders.unitPriceHint')}
              value={unitPrice}
              onChange={setUnitPrice}
              min={0}
              decimalScale={4}
              prefix="$"
            />
          </SimpleGrid>

          {isEdit && line!.quantityShipped > 0 && (
            // The server refuses a reduction below what has already gone, so say so before the
            // round trip rather than after it.
            <Alert color="yellow" variant="light">
              {t('salesOrders.alreadyShipped', { quantity: line!.quantityShipped })}
            </Alert>
          )}

          <Text size="sm" c="dimmed">
            {t('salesOrders.lineTotalIs', {
              total: lineTotal.toLocaleString(undefined, { style: 'currency', currency: 'USD' }),
            })}
          </Text>

          {error && (
            <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
              <Text size="sm">{error}</Text>
            </Alert>
          )}

          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              {t('shell.cancel')}
            </Button>
            <Button type="submit" color="invetrak" loading={save.isPending}>
              {t('shell.saveChanges')}
            </Button>
          </Group>
        </Stack>
      </form>
    </AppModal>
  );
}
